import { z } from 'zod'
import type { UsersListFilters } from '~/composables/useUsers'

export const PASSWORD_MIN_LENGTH = 8

/**
 * Esquema de validación para la creación de usuarios
 */
export const createUserSchema = z
  .object({
    name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
    email: z.email('Email inválido'),
    password: z.string().min(
      PASSWORD_MIN_LENGTH,
      `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`
    ),
    role: z.enum(['admin', 'planta', 'tienda']),
    locationId: z.string().optional()
  })
  .superRefine((data, ctx) => {
    if (data.role === 'tienda' && !data.locationId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Debes seleccionar una tienda para el rol tienda',
        path: ['locationId']
      })
    }
  })

/**
 * Esquema de validación para la actualización de usuarios
 */
export const updateUserSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres').optional(),
  email: z.email('Email inválido').optional(),
  locationId: z.string().nullable().optional()
})

/**
 * Valida actualización según el rol actual del usuario.
 * El rol tienda exige tienda asignada.
 */
export function createUpdateUserSchema(role?: string | null) {
  return updateUserSchema.superRefine((data, ctx) => {
    if (role === 'tienda' && !data.locationId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Debes seleccionar una tienda para el rol tienda',
        path: ['locationId']
      })
    }
  })
}

/**
 * Esquema de validación para el cambio de contraseña (Admin)
 */
export const setUserPasswordSchema = z.object({
  password: z.string().min(
    PASSWORD_MIN_LENGTH,
    `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`
  )
})

export function buildUsersListQuery(filters: UsersListFilters = {}) {
  const search = filters.search?.trim()

  return {
    limit: filters.limit ?? 20,
    offset: filters.offset ?? 0,
    ...(filters.role ? { role: filters.role } : {}),
    ...(search ? { search } : {})
  }
}

export type CreateUserForm = z.infer<typeof createUserSchema>
export type UpdateUserForm = z.infer<typeof updateUserSchema>
export type SetPasswordForm = z.infer<typeof setUserPasswordSchema>
